/* eslint-disable no-console */
import makeUrl, { getDeviceInfoString } from "./util/makeUrl";
import { makeParams, makeTypeMaps } from "./util/params";
import { getDomainCategory, getSourceInformation } from "./util/domainMapping";
import { STR_AGGREGATION_VERSION_TO_FUNCTION_MAP } from "./util/strAggregationFunctions";
import { BRIDGE_KEYS } from "../../constants/bridgeKeys";
import { URLS } from "./const/urls";
import { getDeviceInfo as utilGetDeviceInfo } from "./util/deviceInfo";
import debugTools from "../../evaluation/debug";

// If window.performance isn't available [not on web], use a no-op placeholder instead
const performance = window?.performance?.mark
    ? window.performance
    : {
        mark: () => ({}),
        measure: () => ({}),
    };

const { CONTENT_METADATA_KEY, COORDINATOR_SERVICE_DATA_KEY, ABBA_PLATFORM_FEED_BASE_KEY } = BRIDGE_KEYS;
const { STR_FILE_URL, COORDINATOR_SERVICE_URL, ABBA_FEED_S3_URL_BASE } = URLS;

class ByzantiumCore {
    constructor(
        bridge,
        {
            strFileUrl = STR_FILE_URL,
            coordinatorServiceUrl = COORDINATOR_SERVICE_URL,
            useCachedCoordinatorData = true,
        } = {}) {
        this._bindClassFunctions(this);
        this._bridge = bridge;
        this.strFileUrl = strFileUrl;
        this.coordinatorServiceUrl = coordinatorServiceUrl;
        this.useCachedCoordinatorData = useCachedCoordinatorData;
    }

    _bindClassFunctions(self) {
        self.getTags = self.getTags.bind(self);
        self.getUserInfo = self.getUserInfo.bind(self);
        self.getExtendedAccessUserState = self.getExtendedAccessUserState.bind(self);
        self.injectIFrame = self.injectIFrame.bind(self);
        self.setContentMetadata = self.setContentMetadata.bind(self);
        self.getContentMetadata = self.getContentMetadata.bind(self);
        self.getSTRValues = self.getSTRValues.bind(self);
        self.getUrlValues = self.getUrlValues.bind(self);
        self.getTargetingMetadata = self.getTargetingMetadata.bind(self);
        self.getAdRecommendations = self.getAdRecommendations.bind(self);
        self.refreshCoordinatorServiceData = self.refreshCoordinatorServiceData.bind(self);
        self.fetchCoordinatorServiceData = self.fetchCoordinatorServiceData.bind(self);
        self.refreshFeeds = self.refreshFeeds.bind(self);
        self._fetchAbbaFeeds = self._fetchAbbaFeeds.bind(self);
    }

    async _withUserInfo(fn, params = {}) {
        const userInfo = await this.getUserInfo();
        const finalParams = Object.assign({}, params, { userInfo });

        return fn(finalParams);
    }

    async refreshCoordinatorServiceData({ forceCoordinatorFetch = false, coordinatorQueryParams = {} } = {}) {
        performance.mark("AbbaClient-refreshCoordinatorServiceData-Start");

        const bridgeInfoPromises = [
            this._bridge.getCurrentResource(),
            this._bridge.getPersonalizationAllowed(),
            this._bridge.getReferrer(),
            this._bridge.getQueryParams(),
            this._bridge.needsPersonalizationRefresh(),
        ];

        const bridgeDataValues = await Promise.all(bridgeInfoPromises);
        const { id, contentType } = bridgeDataValues[0];
        const personalizationAllowed = bridgeDataValues[1] || false;
        const referrerSource = getSourceInformation(bridgeDataValues[2], bridgeDataValues[3]);
        const needsRefresh = bridgeDataValues[4] || false;

        const cachedCoordinatorData = await this._bridge.getItem(COORDINATOR_SERVICE_DATA_KEY, false, true);
        let coordinatorData = {};
        let wasBlockingCall = false;

        if (!needsRefresh && !forceCoordinatorFetch && cachedCoordinatorData) {
            coordinatorData = cachedCoordinatorData;
        } else if (this.useCachedCoordinatorData && cachedCoordinatorData) {
            coordinatorData = cachedCoordinatorData;

            // Makes a non-blocking background fetch call [doesn't await it] to update coordinatorData
            this.fetchCoordinatorServiceData({
                baseUrl: this.coordinatorServiceUrl,
                id,
                contentType,
                deviceInfo: utilGetDeviceInfo(),
                referrerSource,
                personalizationAllowed,
                coordinatorQueryParams,
            });
        } else {
            wasBlockingCall = true;

            coordinatorData = await this.fetchCoordinatorServiceData({
                baseUrl: this.coordinatorServiceUrl,
                id,
                contentType,
                deviceInfo: utilGetDeviceInfo(),
                referrerSource,
                personalizationAllowed,
                coordinatorQueryParams,
            });
        }

        performance.mark("AbbaClient-refreshCoordinatorServiceData-End");
        const measure = performance.measure("AbbaClient-refreshCoordinatorServiceData", "AbbaClient-refreshCoordinatorServiceData-Start", "AbbaClient-refreshCoordinatorServiceData-End");

        debugTools.debug(`Resolved refreshCoordinatorServiceData in ${ measure.duration }ms, wasBlockingCall: ${ wasBlockingCall }.`);
        window?.newrelic?.addPageAction("AbbaClient-refreshCoordinatorServiceData", {
            duration: measure.duration,
            wasBlockingCall,
        });

        return {
            data: coordinatorData,
            wasBlockingCall,
        };
    }

    async fetchCoordinatorServiceData(params) {
        performance.mark("AbbaClient-fetchCoordinatorServiceData-Start");

        const coordinatorData = await _fetchWrapper(params);

        if (coordinatorData !== null) {
            const expirationTime = getCoordinatorCacheExpirationDate().getTime();

            await this._bridge.setItem(COORDINATOR_SERVICE_DATA_KEY, coordinatorData, false, expirationTime);
        }

        performance.mark("AbbaClient-fetchCoordinatorServiceData-End");
        performance.measure("AbbaClient-fetchCoordinatorServiceData", "AbbaClient-fetchCoordinatorServiceData-Start", "AbbaClient-fetchCoordinatorServiceData-End");

        return coordinatorData ?? {};
    }

    async refreshFeeds(platform) {
        performance.mark("AbbaClient-refreshFeeds-Start");

        let feedsData = await this._bridge.getItem(`${ ABBA_PLATFORM_FEED_BASE_KEY }${ platform }`, false, true);
        let wasBlockingCall = false;

        if (!feedsData) {
            feedsData = await this._fetchAbbaFeeds(platform);
            wasBlockingCall = true;
        } else {
            this._fetchAbbaFeeds(platform);
        }

        performance.mark("AbbaClient-refreshFeeds-End");
        const measure = performance.measure("AbbaClient-refreshFeeds", "AbbaClient-refreshFeeds-Start", "AbbaClient-refreshFeeds-End");

        debugTools.debug(`Resolved refreshFeeds for platform ${ platform } in ${ measure.duration }ms, wasBlockingCall: ${ wasBlockingCall }.`);
        window?.newrelic?.addPageAction("AbbaClient-refreshFeeds", {
            duration: measure.duration,
            wasBlockingCall,
        });

        return {
            data: feedsData,
            wasBlockingCall,
        };
    }

    async _fetchAbbaFeeds(platform) {
        performance.mark("AbbaClient-_fetchAbbaFeeds-Start");

        const feeds = await _fetchWrapper({
            baseUrl: `${ ABBA_FEED_S3_URL_BASE }/${ platform }.json`,
            limit: null,
        }, { includeCredentials: false });

        if (feeds !== null) {
            // 5 Minutes from now
            const expirationTime = new Date().getTime() + 60 * 5 * 1000;

            await this._bridge.setItem(`${ ABBA_PLATFORM_FEED_BASE_KEY }${ platform }`, feeds, false, expirationTime);
        }

        performance.mark("AbbaClient-_fetchAbbaFeeds-End");
        performance.measure("AbbaClient-_fetchAbbaFeeds", "AbbaClient-_fetchAbbaFeeds-Start", "AbbaClient-_fetchAbbaFeeds-End");

        return feeds ?? {};
    }

    getAdParams(params) {
        return this._withUserInfo(getAdParams, params);
    }

    /**
     * Retrieves ad targeting contextual information.
     * @param {Object} adTargeting additional parameters to be merged with contextual and user preference information
     * @returns {Object} merged result of ad targeting, contextual targets, and user preference targets
     * @param {Object} contentInfo.contentTags
     *  content tags information to be combined with referrer and user info to form contextual and user preference targeting
     */
    async getTags(adTargeting = {}, contentInfo = {}) {
        let adParams = {};

        adParams = getAdParams({
            userInfo: await this.getUserInfo(),
            referrer: await this._bridge.getReferrer(),
            contentTags: contentInfo?.contentTags
                ?? (await this._bridge.getItem(CONTENT_METADATA_KEY, true))?.contentTags
                ?? [],
        });

        return Object.assign({}, adTargeting, adParams);
    }

    /**
     * Retrieves ad recommendations based on the threshold score and targeting param limit
     * @param {Object} threshold minimum threshold of ad recommendation score to consider
     * @param {Object} targetingParamLimit Number of records to return (Top K)
     * @returns {String} comma separated list of ad code recommendations
     */
    async getAdRecommendations(adTargeting = {}, { targetingParamLimit = Number.MAX_SAFE_INTEGER } = {}) {
        const userInfo = await this.getUserInfo();
        const recs = {};
        const recsKeyMapping = {
            "HIGH": "recsh",
            "MEDIUM": "recsm",
            "LOW": "recsl",
        };

        if (userInfo?.Metadata?.prediction?.adPropensity) {
            for (const [key, value] of Object.entries(userInfo.Metadata.prediction.adPropensity)) {
                recs[recsKeyMapping[key]] = value;
            }
        }

        return Object.assign({}, adTargeting, recs);
    }

    injectIFrame(parent, params, baseUrl) {
        if (typeof this._bridge.injectIFrame === "function") {
            this._bridge.injectIFrame(parent, params, baseUrl);
        }
    }

    getPreferredTags(params) {
        return this._withUserInfo(getPreferredTags, params);
    }

    getPreferredResourceTypes() {
        return this._withUserInfo(getPreferredResourceTypes);
    }

    getPreferredAuthors() {
        return this._withUserInfo(getPreferredAuthors);
    }

    getUserSegments() {
        return this._withUserInfo(getUserSegments);
    }

    getUserActions() {
        return this._withUserInfo(getUserActions);
    }

    async getUserMetadata() {
        const { Metadata = {} } = await this.getUserInfo();
        const updatedAttributes = addCalendarMonthSinceFirstVisit(Metadata.attribute);
        const newMetadata = Object.assign({}, Metadata, { attribute: updatedAttributes });

        return { Metadata: newMetadata };
    }

    async getGeoIPData() {
        const coordinatorData = await this._bridge.getItem(COORDINATOR_SERVICE_DATA_KEY);

        return coordinatorData?.geoIP || {};
    }

    async getUserInfo() {
        const personalizationAllowed = await this._bridge.getPersonalizationAllowed();

        if (!personalizationAllowed) {
            return {};
        }

        const coordinatorData = await this._bridge.getItem(COORDINATOR_SERVICE_DATA_KEY);

        return coordinatorData?.personalization || {};
    }

    async getExtendedAccessUserState() {
        const personalizationAllowed = await this._bridge.getPersonalizationAllowed();

        if (!personalizationAllowed) {
            return {};
        }

        const coordinatorData = await this._bridge.getItem(COORDINATOR_SERVICE_DATA_KEY);

        return coordinatorData?.extendedAccessUserState || {};
    }

    async getContextMetadata() {
        // For now context metadata consists of only the referrer domain category
        try {
            const referrer = await this._bridge.getReferrer();

            return {
                Metadata: {
                    attribute: {
                        referrer,
                        domainCategory: getDomainCategory(referrer),
                    },
                },
            };
        } catch (err) {
            console.error(err);

            return {};
        }
    }

    setContentMetadata({
        adCodes = [],
        archived = false,
        brand = "",
        contentTags = [],
        contentType = "",
        franchise = "",
        id = "",
        metadata = {},
        editorialPillar = "",
        recordSlug = "",
    } = {}) {
        return this._bridge.setItem(
            CONTENT_METADATA_KEY,
            {
                adCodes, archived, brand, contentTags, contentType, franchise, id,
                articleMetadata: metadata, pillar: editorialPillar, recordSlug,
            },
            true
        );
    }

    async getContentMetadata() {
        return await this._bridge.getItem(CONTENT_METADATA_KEY, true);
    }

    async getSTRValues() {
        const aggregationMap = {};

        const promises = [
            this._bridge.getItem(CONTENT_METADATA_KEY, true),
            this._bridge.getItem(COORDINATOR_SERVICE_DATA_KEY),
            this._fetchSTRFile(this.strFileUrl),
        ];

        try {
            const values = await Promise.all(promises);
            const adCodes = values[0] ? (values[0].adCodes || []) : [];
            const countryCode = values[1]?.geoIP?.country || "";
            const strFileInfo = values[2];

            Object.keys(STR_AGGREGATION_VERSION_TO_FUNCTION_MAP).forEach((key) => {
                const aggregationFunction = STR_AGGREGATION_VERSION_TO_FUNCTION_MAP[key];

                aggregationMap[key] = aggregationFunction(adCodes, countryCode, strFileInfo);
            });

            return aggregationMap;
        } catch (err) {
            console.error(err);

            return {};
        }
    }

    async _fetchSTRFile(strFileUrl) {
        const response = await fetch(strFileUrl);

        if (!response.ok) {
            throw new Error(response.statusText);
        }

        return response.json();
    }

    /**
     * @typedef {Object} UrlValues
     * @property {String} url - the window Url
     * @property {Array} utmValues - a list of all query parameters that start with "utm_"
     * @returns {Promise<UrlValues>} - Promise containing UrlValues the combined set of values
     */

    async getUrlValues() {
        const urlValues = {
            url: "",
            utmValues: undefined,
            queryParams: undefined,
        };

        const promises = [
            this._bridge.getUrl(),
            this._bridge.getQueryParams("utm_.*"), //just utm_* params
            this._bridge.getQueryParams(), //all query params
        ];

        try {
            const values = await Promise.all(promises);

            urlValues.url = values[0] || "";
            urlValues.utmValues = values[1] || new URLSearchParams();
            urlValues.queryParams = values[2] || new URLSearchParams();
        } catch (err) {
            console.log(err);
        }

        return urlValues;
    }

    /**
     * Retrieves all the information needed by ABBA to evaluate experiments
     *
     * @returns {Promise<Object>} - Promise that resolves to an object containing all the needed targeting information
     */
    async getTargetingMetadata() {
        try {
            const contentMetadata = (await this.getContentMetadata()) || {};
            const promises = [
                this.getContextMetadata(),
                this.getAdParams(contentMetadata.contentTags),
                this.getUrlValues(),
                this.getUserMetadata(),
                this.getGeoIPData(),
            ];

            const [contextInfo, tagsInfo, urlUtmValues, byzantium, geoIP] = await Promise.all(promises);

            return {
                contextInfo,
                tagsInfo,
                urlUtmValues,
                contentMetadata,
                byzantium,
                geoIP,
            };
        } catch (err) {
            console.error(err);

            throw err;
        }
    }

    getDeviceInfo() {
        const deviceInfo = utilGetDeviceInfo();

        return {
            ...deviceInfo,
            deviceInfoString: getDeviceInfoString({ deviceInfo }),
        };
    }

    async getReferrerSource() {
        const bridgeInfoPromises = [
            this._bridge.getReferrer(),
            this._bridge.getQueryParams(),
        ];

        const bridgeDataValues = await Promise.all(bridgeInfoPromises);
        const referrerSource = getSourceInformation(bridgeDataValues[0], bridgeDataValues[1]);

        return referrerSource;
    }

    async getAccesslistMembership() {
        const coordinatorData = await this._bridge.getItem(COORDINATOR_SERVICE_DATA_KEY);

        return coordinatorData?.accesslistMembership ?? "";
    }

    async getUserCredentials() {
        const coordinatorData = await this._bridge.getItem(COORDINATOR_SERVICE_DATA_KEY);

        return coordinatorData?.userCredentials ?? {};
    }
}

//taking in today to simplify unit testing
function calendarMonthsBetween(newDate, oldDate) {
    const [oldYear, oldMonth] = oldDate.split("-"); //drop day

    return (newDate.getFullYear() - parseInt(oldYear)) * 12
        + (newDate.getMonth() + 1 /* 0 based */ - parseInt(oldMonth))
        + 1; // minimum visit is 1 month even if they first came today, so add one
}

function addCalendarMonthSinceFirstVisit(data) {
    const today = new Date();
    const calendarMonthsSinceFirstVisit = data && data.firstVisitedDate ? calendarMonthsBetween(today, data.firstVisitedDate) : 1;

    return Object.assign({}, data, { calendarMonthSinceFirstVisit: calendarMonthsSinceFirstVisit });
}

async function _fetchWrapper(params, { includeCredentials = true } = {}) {
    performance.mark(`AbbaClient-_fetchWrapper-${ params.baseUrl }-Start`);

    const {
        fetchApi = fetch,
    } = params;

    const url = makeUrl(params);

    try {
        const response = await fetchApi(url, {
            credentials: includeCredentials ? "include" : undefined,
        });

        const returnJson = response.json();

        performance.mark(`AbbaClient-_fetchWrapper-${ params.baseUrl }-End`);
        performance.measure(`AbbaClient-_fetchWrapper-${ params.baseUrl }`,
            `AbbaClient-_fetchWrapper-${ params.baseUrl }-Start`,
            `AbbaClient-_fetchWrapper-${ params.baseUrl }-End`);

        return returnJson;
    } catch (error) {
        console.error(`Failed to fetch data from url: ${ url }.`, error);
        window?.newrelic?.addPageAction("AbbaClient-_fetchWrapper-error", {
            url,
            error,
        });

        return null;
    }
}

/**
 *
 * @param {Object} params - the Object containing userInfo, contentTags, and referrer
 * @param {Object} params.userInfo - the user info object obtained from the page
 * @param {Array} params.contentTags - contains the contentTags of the current resource
 * @param {String} params.referrer - the referrer URL for the ad
 *
 * @returns {Object} Ad parameter object containing context info and preferences
 */
function getAdParams(params) {
    const {
        userInfo,
        // Array containing the `contentTags` of the current resource
        contentTags,
        // String referrer,
        referrer,
    } = params;

    return makeParams({
        userInfo,
        contextInfo: {
            referrer,
            contentTags,
        },
    });
    // makeParams will
    //   inspect contentTags, default score when missing
    //   filter based on secret thresholds
    //   return context*, preferred* targeting properties in an Object
}

function getPreferredTags(params) {
    const {
        userInfo,
        // Array containing the `contentTags` of the current resource
        contentTags,
    } = params;

    const {
        preferredTagsTypeMap,
    } = makeTypeMaps({
        userInfo,
        contextInfo: {
            contentTags,
        },
    });

    return preferredTagsTypeMap;
}

function getPreferredResourceTypes(params) {
    const {
        userInfo,
    } = params;

    const {
        preferredResourceTypesMap,
    } = makeTypeMaps({
        userInfo,
    });

    return preferredResourceTypesMap;
}

function getPreferredAuthors(params) {
    const {
        userInfo,
    } = params;

    const {
        preferredAuthorsMap,
    } = makeTypeMaps({
        userInfo,
    });

    return preferredAuthorsMap;
}

function getUserSegments({
    userInfo,
}) {
    return _pluckUserMetadata({
        userInfo,
        targetField: "segment",
        outputField: "userSegments",
    });
}

function getUserActions({
    userInfo,
}) {
    return _pluckUserMetadata({
        userInfo,
        targetField: "action",
        outputField: "userActions",
    });
}

function _pluckUserMetadata({
    userInfo,
    targetField,
    outputField,
}) {
    const {
        Metadata: {
            [targetField]: fieldValue = [],
        } = {},
    } = (userInfo || {});

    return {
        [outputField]: fieldValue.map((s) => s[`${ targetField }Id`]),
    };
}

function getCoordinatorCacheExpirationDate() {
    const expirationDate = new Date();

    // 7PM UTC, this is when lakeview refreshes
    expirationDate.setUTCHours(19, 0, 0, 0);

    // If 7PM UTC today would be in the past, set it for tomorrow's 7PM UTC.
    if (Date.now() > expirationDate.getTime()) {
        expirationDate.setDate(expirationDate.getDate() + 1);
    }

    return expirationDate;
}

export {
    ByzantiumCore,
    _fetchWrapper,
    getAdParams,
    getPreferredAuthors,
    getPreferredResourceTypes,
    getPreferredTags,
    getUserActions,
    getUserSegments,
    calendarMonthsBetween,
    getCoordinatorCacheExpirationDate,
};
